import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { LearnerAuthProvider, LearnerRoute, hasTokens } from "./learnerAuth";
import { learnerPath } from "./_learnerConfig";
import RegistrationWizard from "./learnerRegister";
import LearnerLogin from "./learnerLogin";
import LearnerForgotPassword from "./learnerForgotPassword";
import LearnerSetPassword from "./learnerSetPassword";
import LearnerDashboard from "./learnerDashboard";
import LearnerMyCourses from "./learnerMyCourses";
import LearnerCourse from "./learnerCourse";
import LearnerQuiz from "./learnerQuiz";
import LearnerCertificates from "./learnerCertificates";
import LearnerProfile from "./learnerProfile";
import LmsTenantGate from "./lmsTenantGate";
import LearnerSupport from "./learnerSupport";
import { PortalNotFound } from "../_shared/routeGuards";

const guarded = (el) => <LearnerRoute>{el}</LearnerRoute>;
const tenantPage = (el) => <LmsTenantGate>{el}</LmsTenantGate>;

const LearnerIndex = () => {
  const { orgName } = useParams();
  return (
    <Navigate
      to={learnerPath(orgName, hasTokens() ? "dashboard" : "login")}
      replace
    />
  );
};

// Unknown learner URLs require a valid learner session. Guests are sent to the
// tenant login; authenticated learners see a real 404 instead of being logged
// out or silently returned to login.
const LearnerFallback = () => {
  const { orgName } = useParams();
  return guarded(
    <PortalNotFound
      homePath={learnerPath(orgName, "dashboard")}
      portalName="learning"
    />
  );
};

// LMS learner surface, mounted at /e-learning/:orgName/*: the tenant
// short name is carried in the parent path, so child routes are relative. Public
// auth routes plus the authenticated portal (dashboard, courses, player, profile,
// support) behind the LearnerRoute guard.
const LmsLearnerRouter = () => (
  <LearnerAuthProvider>
    <Routes>
      <Route index element={tenantPage(<LearnerIndex />)} />
      <Route path="login" element={tenantPage(<LearnerLogin />)} />
      <Route path="register" element={tenantPage(<RegistrationWizard />)} />
      <Route path="forgot-password" element={tenantPage(<LearnerForgotPassword />)} />
      {/* Activation and reset links both resolve here (token+email in the query). */}
      <Route path="set-password" element={tenantPage(<LearnerSetPassword />)} />
      <Route path="reset-password" element={tenantPage(<LearnerSetPassword />)} />

      <Route path="dashboard" element={tenantPage(guarded(<LearnerDashboard />))} />
      <Route path="my-courses" element={tenantPage(guarded(<LearnerMyCourses />))} />
      <Route path="course/:courseId/player" element={tenantPage(guarded(<LearnerCourse />))} />
      <Route path="course/:courseId" element={tenantPage(guarded(<LearnerCourse />))} />
      {/* Dedicated full-screen assessment page */}
      <Route path="course/:courseId/quiz/:quizId" element={tenantPage(guarded(<LearnerQuiz />))} />
      <Route path="certificates" element={tenantPage(guarded(<LearnerCertificates />))} />
      <Route path="profile" element={tenantPage(guarded(<LearnerProfile />))} />
      <Route path="help&support" element={tenantPage(guarded(<LearnerSupport />))} />

      <Route path="*" element={tenantPage(<LearnerFallback />)} />
    </Routes>
  </LearnerAuthProvider>
);

export default LmsLearnerRouter;
